import {
  appendDataLayerValue,
  trackFormSubmit,
  trackLeadSubmit,
} from 'utils/trackingFunctions';
import { getUsersGoogleSessionIds } from 'app-requests/triadmsRequests';
import {
  getPageViewId,
  getUserSessionId,
  setLeadGuid,
} from 'utils/analyticsHelpers';
import { LogError } from 'utils/logging/LogError';
import { LogInfo, newrelicPageAction } from 'utils/logging';
import { TRIAD_PROXY_ROUTE } from 'app-requests/apiConstants';
import { getDOMFieldValue } from 'utils/formValuesUtils';
import { formValuesToRequestArr } from 'utils/form-utils/formValuesToRequestArr';
import {
  FormValues,
  LeadSubmitRawResponse,
  LeadSubmitResponse,
  Question,
  ProfileGuid,
  ErrorPriority,
  LeadGuid,
} from 'types';
import { FIELD_NAMES } from 'consts';
import { triadSuperFetch } from 'utils/superFetch';

interface FormConfigs {
  leadSubmitEndpoint?: string;
  leadEvalToken?: string;
  disclaimerText?: string;
  schoolCode: string;
  variant?: string;
}

let didSubmitThisSession = false;

/**
 * @summary This function is used to submit the lead for micro sites
 */
export async function submitMicroSiteLead(
  formValues: FormValues,
  fieldNameMap: Record<FIELD_NAMES, Question>,
  formConfigs: FormConfigs,
  metaData: { isClickUser: boolean }
): Promise<LeadSubmitResponse> {
  const { isClickUser } = metaData || {};
  const {
    schoolCode,
    variant,
    leadSubmitEndpoint = '/leadSubmit',
    disclaimerText,
    leadEvalToken,
  } = formConfigs;

  trackFormSubmit();

  // TODO: once backend is live with Conversion Tracking there is refactor needed so frontend does not send conversion events
  const metadata = getUsersGoogleSessionIds();

  if (!getUserSessionId() || !getPageViewId()) {
    LogError('No UserSessionId or PageViewId Found when submitting form', {
      priority: ErrorPriority.ONE,
      description: `sessionId: ${getUserSessionId()}, pageViewId: ${getPageViewId()}`,
    });
  }

  newrelicPageAction('lead_submit', { requestStatus: 'started' });
  LogInfo('lead_submit', { description: 'started' });

  if (didSubmitThisSession) {
    LogError('Lead already submitted this session', {
      priority: ErrorPriority.THREE,
      description: 'Lead already submitted this session',
    });
  }

  didSubmitThisSession = true;

  try {
    const [apiError, response] = await triadSuperFetch<LeadSubmitRawResponse>(
      `${TRIAD_PROXY_ROUTE}${leadSubmitEndpoint}`,
      {
        method: 'POST',
        timeout: 120000,
        retry: {
          maxAttempts: 3,
          waitTimeBetweenFails: 1000,
        },
        body: {
          schoolCode,
          templateName: variant,
          tcpaText: disclaimerText,
          trustedFormUrl: getDOMFieldValue('xxTrustedFormCertUrl_0'),
          leadId: getDOMFieldValue('leadid_token'),
          questionReplies: formValuesToRequestArr(formValues, fieldNameMap),
          isClickUser,
          metadata,
          leadEvalToken,
        },
      }
    );

    if (apiError) {
      throw apiError;
    }

    const { status, result: rawResult } = response;

    const result = rawResult ?? {
      ProfileId: '' as ProfileGuid,
      LeadGuid: '' as LeadGuid,
      Message: '',
      Revenue: 0,
      AdjustedRevenue: 0,
      Links: { ApplicationLink: '' },
    };

    // For a single lead submit, mainly micro sites
    appendDataLayerValue('profileId', result.ProfileId ?? 'NOT_FOUND');

    const isTestMode = result.Message?.includes('TEST MODE;');
    const isAccepted = result.Message?.startsWith('ACCEPTED');
    const isPending = result.Message?.includes('PENDING');
    const shouldTrackConversion = status === 'ok' && isAccepted && !isTestMode;

    if (shouldTrackConversion) {
      newrelicPageAction('lead_submit', {
        requestStatus: isPending ? 'success-pending' : 'success-accepted',
        isTestMode: isTestMode ? 'true' : 'false',
      });
      if (!isTestMode) {
        LogInfo('lead_submit', {
          description: isPending ? 'success-pending' : 'success-accepted',
        });
      }
    } else {
      newrelicPageAction('lead_submit', {
        requestStatus: 'success-rejected',
        isTestMode: isTestMode ? 'true' : 'false',
      });
      if (!isTestMode) {
        LogInfo('lead_submit', { description: 'success-rejected' });
      }
    }

    setLeadGuid(result.LeadGuid);

    await trackLeadSubmit(formValues, {
      isTestMode,
      isPending,
      isAccepted,
      shouldTrackConversion,
    });

    return {
      shouldTrackConversion,
      profileGuid: result.ProfileId,
      leadsSubmittedFor: [
        {
          leadGuid: result.ProfileId,
          statusMessage: result.Message,
          status,
          revenue: result.Revenue,
          adjustedRevenue: result.AdjustedRevenue,
          schoolCode,
          links: { applicationLink: result.Links?.ApplicationLink ?? '' },
        },
      ],
    };
  } catch (error: unknown) {
    newrelicPageAction('lead_submit', { requestStatus: 'failed' });
    LogInfo('lead_submit', { description: 'failed' });

    const error_ = error as Error & { trace?: string };

    LogError(`submitLead ${error_.message}`, {
      priority: ErrorPriority.ONE,
      description: `trace: ${error_.trace}, endpoint: ${leadSubmitEndpoint}`,
      endpoint: leadSubmitEndpoint,
    });
    throw error;
  }
}
